
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner, Modal } from "react-bootstrap";
import {
  Users,
  Clapperboard,
  Star,
  TrendingUp,
  MoreVertical,
  Plus,
  Eye,
  Film,
  Video,
  UserCheck
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Swal from "sweetalert2";
import "./CastingDirectorDashboard.css";
import { fetchCastingDirectorDashboardApi, followUserApi, removeFollowerApi  } from "../../../services/api";

const CastingDirectorDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowersModal, setShowFollowersModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetchCastingDirectorDashboardApi();
      setDashboardData(response.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };


    const handleRemoveFollower = async (talentId, talentName) => {
    try {
      // ✅ Calls dedicated removeFollowerApi
      const res = await removeFollowerApi(talentId);

      if (res.data?.success || res.success) {
        setDashboardData((prev) => {
          if (!prev) return prev;
          const updatedFollowers = (prev.followers || []).filter((f) => f.id !== talentId);
          return {
            ...prev,
            kpis: { ...prev.kpis, followersCount: updatedFollowers.length },
            followers: updatedFollowers
          };
        });

        Swal.fire({
          icon: "success",
          title: "Follower Removed",
          text: `Removed ${talentName} from your followers list.`,
          width: "300px",
          background: "#131824",
          color: "#ffffff",
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (err) {
      console.error("Remove follower error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to remove follower",
        width: "300px",
        background: "#131824",
        color: "#ffffff"
      });
    }
  };

  const formatCamelCase = (text) => {
    if (!text || text === "Casting Director" || text === "casting_director" || text === "Candidate") return "Candidate";
    return String(text).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const filteredApplications =
    activeTab === "all"
      ? dashboardData?.recentApplications || []
      : dashboardData?.recentApplications?.filter((item) => {
          const status = String(item?.status || '').toLowerCase();
          if (activeTab === "pending") {
            return status === "pending" || status === "applied";
          }
          if (activeTab === "shortlisted") {
            return (
              status === "shortlisted" ||
              status === "selected"
            );
          }
          return true;
        }) || [];

  const getStatusBadge = (status = '') => {
    const s = String(status || '').toLowerCase();
    if (s === "pending" || s === "applied") return <span className="cdd-badge badge-yellow">PENDING</span>;
    if (s === "resubmit") return <span className="cdd-badge cdd-badge-resubmit">RESUBMIT</span>;
    if (s === "reviewed") return <span className="cdd-badge badge-blue">REVIEWED</span>;
    if (s === "shortlisted" || s === "selected") return <span className="cdd-badge badge-green">SHORTLISTED</span>;
    if (s === "rejected") return <span className="cdd-badge badge-red">REJECTED</span>;
    return <span className="cdd-badge badge-gray">{String(status || 'PENDING').toUpperCase()}</span>;
  };

  const getTypeBadge = (type = '') => {
    const t = String(type || '').toUpperCase();
    if (t.includes("ONLINE")) return <span className="cdd-type-badge type-online"><Video size={12}/> ONLINE</span>;
    if (t.includes("SLOT_BOOKING") || t.includes("WALK")) return <span className="cdd-type-badge type-walkin">WALK IN</span>;
    return <span className="cdd-type-badge type-gray">{t || 'ONLINE'}</span>;
  };

  const COLOR_MAP = {
    Shortlisted: "#00c781",
    Selected: "#00c781",
    Pending: "#ffb020",
    Applied: "#ffb020",
    Resubmit: "#a855f7",
    RESUBMIT: "#a855f7",
    Rejected: "#ff4d4f"
  };

  const pieChartData = (dashboardData?.chartData || []).filter(
    (item) => item.value > 0
  );

  const totalApplicantsCount = (dashboardData?.chartData || []).reduce(
    (acc, curr) => acc + (Number(curr.value) || 0), 
    0
  );

  if (loading) {
    return (
      <div className="cdd-loading-container">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3">Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="cdd-wrapper">
      <div className="cdd-container">
        
        {/* HEADER */}
        <div className="cdd-header">
          <div>
            <h1 className="cdd-title">Casting Dashboard</h1>
            <p className="cdd-subtitle">Manage your projects, roles, and review incoming talent.</p>
          </div>
          
          <div className="cdd-header-right-wrapper">
            {/* FOLLOWERS TILE */}
            <div 
              className="cdd-followers-tile" 
              onClick={() => setShowFollowersModal(true)} 
              title="Click to view followers"
            >
              <div className="cdd-followers-icon-box">
                <Users size={16} color="#3b82f6" />
              </div>
              <div className="cdd-followers-info">
                <span className="cdd-followers-label">Followers</span>
                <span className="cdd-followers-count">
                  {dashboardData?.kpis?.followersCount ?? dashboardData?.followers?.length ?? 0} Talents
                </span>
              </div>
            </div>

            {/* POST NEW ROLE BUTTON */}
            <button className="cdd-btn-primary" onClick={() => navigate('/dashboard/post-roles')}>
              <Plus size={18} /> Post New Role
            </button>
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="cdd-kpi-grid">
          {[
            {
              title: "Total Projects",
              value: dashboardData?.kpis?.totalProjects || 0,
              icon: <Clapperboard />,
              trend: "+2 this month"
            },
            {
              title: "Active Roles",
              value: dashboardData?.kpis?.activeRoles || 0,
              icon: <Film />,
              trend: "Active"
            },
            {
              title: "Total Applicants",
              value: dashboardData?.kpis?.totalApplicants || 0,
              icon: <Users />,
              trend: "+15%"
            },
            {
              title: "Shortlisted",
              value: dashboardData?.kpis?.shortlisted || 0,
              icon: <Star />,
              trend: "Selected"
            }
          ].map((kpi, index) => (
            <div className="cdd-kpi-card" key={index}>
              <div className="cdd-kpi-top">
                <div className="cdd-kpi-icon">{kpi.icon}</div>
                <div className="cdd-kpi-trend">
                  <TrendingUp size={14} /> <span>{kpi.trend}</span>
                </div>
              </div>
              <h3 className="cdd-kpi-value">{kpi.value}</h3>
              <p className="cdd-kpi-label">{kpi.title}</p>
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION: CHART & ACTIVE ROLES */}
        <div className="cdd-middle-grid">
          
          {/* APPLICANT CHART */}
          <div className="cdd-card cdd-chart-card">
            <div className="cdd-card-header">
              <h2>Applicants Overview</h2>
              <select className="cdd-select">
                <option>Last 7 Days</option>
                <option>This Month</option>
              </select>
            </div>
            
            <div className="cdd-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        const fillColor = COLOR_MAP[data.name] || "#3b82f6";
                        return (
                          <div className="cdd-tooltip-box">
                            <div className="cdd-tooltip-header">
                              <span className="cdd-tooltip-dot" style={{ backgroundColor: fillColor }}></span>
                              <p className="cdd-tooltip-title">{data.name}</p>
                            </div>
                            <p className="cdd-tooltip-val">
                              Count: <span>{data.value}</span>
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />

                  <Pie
                    data={pieChartData.length > 0 ? pieChartData : [{ name: "No Data", value: 1 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={68}
                    outerRadius={92}
                    paddingAngle={4}
                    dataKey="value"
                    nameKey="name"
                    cornerRadius={5}
                    stroke="none"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLOR_MAP[entry.name] || "#3B82F6"}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Centered Total Count Overlay */}
              <div className="cdd-pie-center-overlay">
                <span className="cdd-pie-center-value">{totalApplicantsCount}</span>
                <span className="cdd-pie-center-label">Total</span>
              </div>
            </div>

            {/* Custom Stat Legend Pills */}
            <div className="cdd-legend-container">
              <div className="cdd-legend-item">
                <span className="cdd-legend-dot dot-yellow"></span>
                <span className="cdd-legend-text">Pending</span>
              </div>
              <div className="cdd-legend-item">
                <span className="cdd-legend-dot dot-purple"></span>
                <span className="cdd-legend-text">Resubmit</span>
              </div>
              <div className="cdd-legend-item">
                <span className="cdd-legend-dot dot-green"></span>
                <span className="cdd-legend-text">Shortlisted</span>
              </div>
              <div className="cdd-legend-item">
                <span className="cdd-legend-dot dot-red"></span>
                <span className="cdd-legend-text">Rejected</span>
              </div>
            </div>
          </div>

          {/* ACTIVE ROLES LIST */}
          <div className="cdd-card cdd-roles-card">
            <div className="cdd-card-header">
              <h2>My Active Roles</h2>
              <Link to="/dashboard/roles" className="cdd-link-btn">View All</Link>
            </div>
            <div className="cdd-roles-list">
              {dashboardData?.activeRoles?.map((role) => (
                <div className="cdd-role-item" key={role.id}>
                  <div className="cdd-role-info">
                    <div className="cdd-role-icon-small"><Film size={16} /></div>
                    <div>
                      <h4 className="cdd-role-title">{role.title}</h4>
                      <p className="cdd-role-project">{role.project}</p>
                    </div>
                  </div>
                  <div className="cdd-role-stats">
                    <div className="cdd-applicant-count">
                      <strong>{role.applicants}</strong>
                      <span>Applicants</span>
                    </div>
                    <button 
                      className="cdd-icon-btn" 
                      title="Edit Role"
                      onClick={() => navigate(`/dashboard/post-roles/${role.id}`)}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: RECENT AUDITIONS TABLE */}
        <div className="cdd-card cdd-table-card">
          <div className="cdd-card-header">
            <h2>Recent Applications</h2>
            <div className="cdd-table-filters">
              <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All</button>
              <button className={activeTab === "pending" ? "active" : ""} onClick={() => setActiveTab("pending")}>Pending</button>
              <button className={activeTab === "shortlisted" ? "active" : ""} onClick={() => setActiveTab("shortlisted")}>Shortlisted</button>
            </div>
          </div>

          <div className="cdd-table-responsive">
            <table className="cdd-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>CANDIDATE NAME</th>
                  <th>ROLE TITLE</th>
                  <th>PROJECT</th>
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((row) => (
                  <tr key={row.id || row.roleApplicationId}>
                    <td className="cdd-text-muted">{row.date}</td>
                    <td>
                      <div className="cdd-candidate-cell">
                        <div className="cdd-avatar">{row.candidate ? String(row.candidate).charAt(0) : 'U'}</div>
                        <span className="cdd-candidate-name">{row.candidate}</span>
                      </div>
                    </td>
                    <td className="cdd-font-medium">{row.role}</td>
                    <td className="cdd-text-muted">{row.project}</td>
                    <td>{getTypeBadge(row.type)}</td>
                    <td>{getStatusBadge(row.status)}</td>
                    <td>
                      <button
                        className="cdd-eye-btn"
                        title="View Application"
                        onClick={() =>
                          navigate(`/dashboard/manage-applications/${row.id || row.roleApplicationId}`)
                        }
                      >
                        <Eye size={18} color="#ffffff" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ✅ FOLLOWERS MODAL (ONLY APPLICATIONS STAT BOX + CANDIDATE BADGE) */}
      <Modal show={showFollowersModal} onHide={() => setShowFollowersModal(false)} centered dialogClassName="cd-following-modal-dialog" contentClassName="cdd-modal-content">
        <Modal.Header closeButton closeVariant="white" className="cdd-modal-header">
          <Modal.Title className="cdd-modal-title">
            <Users size={18} className="text-primary" /> Followers (Talents Following You)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cd-modal-body cdd-modal-body">
          {dashboardData?.followers?.length > 0 ? (
            <div className="cdd-modal-list">
              {dashboardData.followers.map((talent) => (
                <div key={talent.id} className="cdd-modal-card">
                  
                  {/* TOP ROW */}
                  <div className="cdd-modal-card-top">
                    <div className="cdd-modal-card-user">
                      <div 
                        className="cdd-modal-avatar"
                        onClick={() => {
                          setShowFollowersModal(false);
                          navigate(`/dashboard/talent-profile/${talent.id}`);
                        }}
                      >
                        {talent.img ? <img src={talent.img} alt={talent.name} className="w-100 h-100" style={{ objectFit: 'cover' }} /> : talent.name.charAt(0)}
                      </div>
                      
                      <div className="cdd-modal-user-meta">
                        <div className="cdd-modal-name-row">
                          <h6 
                            className="cdd-modal-name"
                            onClick={() => {
                              setShowFollowersModal(false);
                              navigate(`/dashboard/talent-profile/${talent.id}`);
                            }}
                          >
                            {talent.name}
                          </h6>
                          {/* ✅ BADGE DISPLAYING CANDIDATE */}
                          <span className="cdd-modal-badge">
                            <UserCheck size={10} className="me-1" color="#ffffff" /> Candidate
                          </span>
                        </div>
                        <small className="cdd-modal-subtext">
                          Candidate
                        </small>
                      </div>
                    </div>

                    {/* REMOVE FOLLOWER BUTTON */}
                    <button 
                      className="cdd-modal-remove-btn"
                      onClick={() => handleRemoveFollower(talent.id, talent.name)}
                    >
                      Remove Follower
                    </button>
                  </div>

                  {/* BOTTOM STAT CARD (ONLY APPLICATIONS) */}
                  <div className="cdd-modal-stat-row">
                    <div className="cdd-stat-box">
                      <div className="cdd-stat-icon-circle-blue">
                        <Film size={14} color="#38bdf8" />
                      </div>
                      <div>
                        <div className="cdd-stat-num text-info">{talent.appliedCount || 0}</div>
                        <div className="cdd-stat-lbl text-secondary">Applications</div>
                      </div>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          ) : (
            <div className="cdd-modal-empty">
              <Users size={28} className="mb-2 opacity-50" />
              <p className="mb-0 small">No talents are following your profile yet.</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default CastingDirectorDashboard;